/**
 * Bundle upsell popup (any product page).
 *
 * When a customer adds the page's own product on a SUBSCRIPTION plan, this offers
 * the companion product the merchant picked in the theme editor, on the same
 * delivery interval, before the cart drawer opens. It runs in both directions:
 * the toilet-paper page offers the kitchen paper, the kitchen-paper page offers
 * the toilet paper. Nothing here is hardcoded to either — "kp" in the selectors
 * is historical and now just means "the companion product being offered".
 *
 * Flow (chosen for robustness over cleverness): the page's own product line is
 * added normally by the section's own add-to-cart handler, which then hands the
 * cart/add.js response to `window.mjuktBeforeCartDrawer` instead of rendering
 * the drawer directly. This module owns that hook: it either resolves straight
 * away (drawer opens as usual) or shows the popup first and resolves once the
 * customer has accepted or declined. Nothing is intercepted before that first
 * line is safely in the cart, so a failure anywhere in here can never cost the
 * customer the add they actually asked for.
 *
 * It stays silent unless every one of these holds:
 *  - the popup block is enabled and resolves a companion product with a variant
 *  - a subscription plan is selected (a one-time purchase never triggers it)
 *  - that plan's delivery interval is one the add-on copy supports, and the
 *    companion product exposes a selling plan on the same interval (so a DRAFT or
 *    planless companion product means no popup at all, never a broken one)
 *  - the cart does not already hold the companion (the inline add-on checkbox
 *    may have put it there already)
 *  - the customer has not declined this companion earlier in the session
 *
 * Interval matching is not reimplemented here: it comes from window.MjuktKpPlans,
 * published by mjukt-kp-addon.js, so both features read Kaching's structured
 * delivery JSON the same way and cannot drift apart.
 */
(function () {
  // Namespaced per companion product. Both product pages carry a popup, and each
  // offers the other one: a customer who says "nei takk" to the kitchen paper on
  // the toilet-paper page has said nothing about the toilet paper, so one decline
  // must not silence the opposite offer for the rest of the session.
  var DECLINE_KEY_BASE = 'mjukt_kp_popup_declined';
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // sessionStorage throws in private-mode Safari and in some embedded webviews.
  // A storage failure must degrade to "not declined" (popup may show) rather
  // than to a thrown error that breaks the add-to-cart chain.
  function hasDeclined(key) {
    try {
      return window.sessionStorage.getItem(key) === '1';
    } catch (e) {
      return false;
    }
  }

  function rememberDecline(key) {
    try {
      window.sessionStorage.setItem(key, '1');
    } catch (e) {
      /* no session storage: the in-page `handled` flag still stops a re-fire */
    }
  }

  ready(function () {
    var root = document.querySelector('[data-kp-popup]');
    if (!root) return;
    if (root.getAttribute('data-kp-popup-enabled') !== 'true') return;
    if (!window.Shopify || !window.Shopify.routes) return;

    var helpers = window.MjuktKpPlans;
    if (!helpers) return;

    var kpHandle = root.getAttribute('data-kp-product-handle');
    var kpVariantId = root.getAttribute('data-kp-variant-id');
    var kpProductId = root.getAttribute('data-kp-product-id');
    if (!kpHandle || !kpVariantId) return;

    var declineKey = DECLINE_KEY_BASE + ':' + kpHandle;

    // The section this markup ships in is scroll-revealed with a transform, and
    // a transformed ancestor turns `position: fixed` into a containing block.
    // Reparenting to <body> makes the overlay independent of that (and of any
    // future animation wrapper) rather than relying on reveal timing.
    if (root.parentNode !== document.body) document.body.appendChild(root);

    var card = root.querySelector('[data-kp-popup-card]');
    var acceptBtn = root.querySelector('[data-kp-popup-accept]');
    var statusEl = root.querySelector('[data-kp-popup-status]');
    var priceEl = root.querySelector('[data-kp-popup-price]');
    if (!card || !acceptBtn) return;

    // Optional per-interval price line, e.g. data-kp-popup-price-4w. Looked up by
    // week count rather than branched on, so the block covers any interval the
    // merchant has written copy for. Absent setting: the element is not rendered
    // at all and the popup shows only its fixed body text.
    function priceLineFor(weeks) {
      return root.getAttribute('data-kp-popup-price-' + weeks + 'w') || '';
    }

    var tpForm = document.getElementById('mjukt-product-form');
    var tpPlans = helpers.flattenPlans(
      helpers.readJsonScript(document.querySelector('[data-tp-selling-plans]'))
    );

    var kpFetchPromise = null;
    var handled = false; // one interception per page load
    var open = false;
    var lastFocused = null;
    var settle = null; // resolves the promise the add-to-cart handler is waiting on

    function fetchKpPlans() {
      if (kpFetchPromise) return kpFetchPromise;
      kpFetchPromise = fetch(window.Shopify.routes.root + 'products/' + kpHandle + '.js', {
        headers: { Accept: 'application/json' }
      })
        .then(function (res) {
          return res.ok ? res.json() : null;
        })
        .then(function (data) {
          if (!data) return [];
          if (data.variants && data.variants[0] && data.variants[0].id) {
            kpVariantId = data.variants[0].id;
          }
          return helpers.flattenPlans(data.selling_plan_groups);
        })
        .catch(function () {
          return [];
        });
      return kpFetchPromise;
    }

    function getSelectedTpPlanId() {
      if (tpForm) {
        var field = tpForm.querySelector('[name="selling_plan"]');
        if (field && field.value) return field.value;
      }
      var fallback = document.querySelector('[name="selling_plan"]');
      return fallback && fallback.value ? fallback.value : null;
    }

    function findTpPlanById(id) {
      var matches = (tpPlans || []).filter(function (p) {
        return String(p.id) === String(id);
      });
      return matches[0] || null;
    }

    // Resolves true when the kitchen paper is already a cart line. A failed or
    // unreadable cart resolves TRUE on purpose: suppressing the offer costs one
    // upsell, whereas guessing "not in cart" risks a duplicate subscription line.
    function cartHasKp() {
      return fetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (res) {
          return res.ok ? res.json() : null;
        })
        .then(function (cart) {
          if (!cart || !Array.isArray(cart.items)) return true;
          for (var i = 0; i < cart.items.length; i++) {
            var item = cart.items[i];
            if (String(item.id) === String(kpVariantId)) return true;
            if (kpProductId && String(item.product_id) === String(kpProductId)) return true;
          }
          return false;
        })
        .catch(function () {
          return true;
        });
    }

    function getSectionsToRender() {
      var cartDrawerEl = document.querySelector('cart-drawer');
      if (cartDrawerEl && typeof cartDrawerEl.getSectionsToRender === 'function') {
        return cartDrawerEl.getSectionsToRender().map(function (s) {
          return s.id;
        });
      }
      return [];
    }

    function setStatus(msg) {
      if (!statusEl) return;
      statusEl.textContent = msg || '';
      statusEl.hidden = !msg;
    }

    function focusables() {
      return Array.prototype.filter.call(card.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });
    }

    function onKeydown(e) {
      if (!open) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        decline();
        return;
      }
      if (e.key !== 'Tab') return;
      var items = focusables();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function closePopup(result) {
      if (!open) return;
      open = false;
      root.hidden = true;
      root.classList.remove('is-open');
      document.removeEventListener('keydown', onKeydown, true);
      document.documentElement.classList.remove('mjukt-kp-popup-open');
      if (lastFocused && typeof lastFocused.focus === 'function') {
        try {
          lastFocused.focus();
        } catch (e) {
          /* the element may have been re-rendered out from under us */
        }
      }
      var done = settle;
      settle = null;
      if (done) done(result);
    }

    function decline() {
      rememberDecline(declineKey);
      closePopup(null);
    }

    function accept(planId, fallbackData) {
      acceptBtn.disabled = true;
      setStatus(root.getAttribute('data-kp-popup-busy-text') || '');

      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: kpVariantId, quantity: 1, selling_plan: planId }],
          sections: getSectionsToRender(),
          sections_url: window.location.pathname
        })
      })
        .then(function (res) {
          return res.json();
        })
        .then(function (data) {
          if (!data || data.status) {
            acceptBtn.disabled = false;
            setStatus(root.getAttribute('data-kp-popup-error-text') || '');
            return;
          }
          // Let the inline add-on checkbox re-read the cart and tick itself,
          // so the two controls never disagree about what is in the cart.
          document.dispatchEvent(new CustomEvent('mjukt:kp-cart-changed'));
          setStatus('');
          // Resolve with the kitchen-paper response: its `sections` payload is
          // the newer one and renders a drawer containing both lines.
          closePopup(data && data.sections ? data : fallbackData);
        })
        .catch(function () {
          acceptBtn.disabled = false;
          setStatus(root.getAttribute('data-kp-popup-error-text') || '');
        });
    }

    function show(planId, addData, weeks) {
      return new Promise(function (resolve) {
        settle = function (result) {
          resolve(result || addData);
        };
        lastFocused = document.activeElement;
        acceptBtn.disabled = false;
        setStatus('');
        if (priceEl) {
          var priceLine = priceLineFor(weeks);
          priceEl.textContent = priceLine;
          priceEl.hidden = !priceLine;
        }
        root.hidden = false;
        root.classList.add('is-open');
        document.documentElement.classList.add('mjukt-kp-popup-open');
        open = true;
        document.addEventListener('keydown', onKeydown, true);

        acceptBtn.onclick = function () {
          accept(planId, addData);
        };
        Array.prototype.forEach.call(root.querySelectorAll('[data-kp-popup-decline]'), function (el) {
          el.onclick = function () {
            decline();
          };
        });

        window.setTimeout(function () {
          if (open) acceptBtn.focus();
        }, 0);
      });
    }

    // Resolving `false` tells the add-to-cart handler not to render the drawer
    // at all. Only used while a popup is already on screen: rendering there
    // would drop the drawer open behind the modal. Every other silent path
    // resolves the payload so the drawer opens exactly as it always did.
    function decide(addData) {
      if (open) return Promise.resolve(false);
      if (handled || hasDeclined(declineKey)) return Promise.resolve(addData);

      var selectedId = getSelectedTpPlanId();
      if (!selectedId) return Promise.resolve(addData); // engangskjøp

      var tpPlan = findTpPlanById(selectedId);
      var weeks = tpPlan ? helpers.planWeeks(tpPlan) : null;
      if (!weeks || (helpers.isSupportedWeeks && !helpers.isSupportedWeeks(weeks))) {
        return Promise.resolve(addData);
      }

      return fetchKpPlans()
        .then(function (plans) {
          var matches = (plans || []).filter(function (p) {
            return helpers.planWeeks(p) === weeks;
          });
          var kpPlan = matches[0] || null;
          if (!kpPlan) return addData; // draft or planless: stay silent

          return cartHasKp().then(function (already) {
            if (open) return false;
            if (already || handled) return addData;
            handled = true;
            return show(kpPlan.id, addData, weeks);
          });
        })
        .catch(function () {
          return addData;
        });
    }

    // The section's add-to-cart handler awaits this before opening the drawer.
    window.mjuktBeforeCartDrawer = function (addData) {
      try {
        return decide(addData);
      } catch (e) {
        return Promise.resolve(addData);
      }
    };
  });
})();
